import { useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen,
  CalendarDays,
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
  MoreVertical,
  PlayCircle,
  Search,
  Trash2,
  UploadCloud,
  Video,
  X,
} from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle, useDeferredFilters, SearchButton } from "@/components/Shared";

const CATEGORY_META = {
  "SOP Instalasi": { icon: BookOpen, tone: "emerald" },
  "SOP Dokumentasi": { icon: ImageIcon, tone: "amber" },
  "Video Tutorial": { icon: PlayCircle, tone: "violet" },
  "Panduan Aplikasi": { icon: FileText, tone: "blue" },
  Administrasi: { icon: FileText, tone: "rose" },
  "Dokumen Pendukung": { icon: FileText, tone: "slate" },
  Lainnya: { icon: FileText, tone: "slate" },
};

const toneClasses = {
  emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  amber: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  violet: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  blue: "border-blue-500/30 bg-blue-500/10 text-blue-300",
  rose: "border-rose-500/30 bg-rose-500/10 text-rose-300",
  slate: "border-slate-600 bg-slate-800/60 text-slate-300",
};

function formatSize(bytes) {
  if (!bytes) return "-";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fileKind(mime, filename) {
  const ext = String(filename || "").split(".").pop()?.toLowerCase();
  if (mime?.includes("video") || ["mp4", "mov", "avi", "mkv"].includes(ext)) return "video";
  if (mime?.includes("image") || ["jpg", "jpeg", "png", "webp"].includes(ext)) return "image";
  return "document";
}

export default function LMS() {
  const { user } = useAuth();
  const isAdmin = user?.role === "SUPER_ADMIN";
  const inputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const { draft: filterDraft, applied: appliedFilters, set: setFilter, apply: applyFilters } = useDeferredFilters({
    search: "",
    category: "ALL",
    sort: "NEWEST",
  });
  const search = filterDraft.search;
  const category = filterDraft.category;
  const sort = filterDraft.sort;
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [openUpload, setOpenUpload] = useState(false);
  const [form, setForm] = useState({ judul: "", kategori: "SOP Instalasi", deskripsi: "", file: null, youtube_url: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lms/materials");
      setItems(data.items || []);
      setCategories(data.categories || []);
    } catch {
      toast.error("Gagal memuat materi LMS");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(() => {
    const result = { ALL: items.length };
    items.forEach((item) => {
      result[item.kategori] = (result[item.kategori] || 0) + 1;
    });
    return result;
  }, [items]);

  const filtered = useMemo(() => {
    const keyword = String(appliedFilters.search || "").trim().toLowerCase();
    const rows = items.filter((item) => {
      const matchCategory = appliedFilters.category === "ALL" || item.kategori === appliedFilters.category;
      const text = `${item.judul} ${item.deskripsi} ${item.kategori}`.toLowerCase();
      return matchCategory && (!keyword || text.includes(keyword));
    });
    return [...rows].sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return sort === "NEWEST" ? db - da : da - db;
    });
  }, [items, appliedFilters]);

  const upload = async (e) => {
    e.preventDefault();
    if (!form.judul.trim() || (!form.file && !form.youtube_url.trim())) {
      toast.error("Judul dan file atau link YouTube wajib diisi");
      return;
    }
    const fd = new FormData();
    fd.append("judul", form.judul.trim());
    fd.append("kategori", form.kategori);
    fd.append("deskripsi", form.deskripsi.trim());
    fd.append("youtube_url", form.youtube_url.trim());
    if (form.file) fd.append("file", form.file);

    setUploading(true);
    try {
      await api.post("/lms/materials", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Materi LMS berhasil diupload");
      setForm({ judul: "", kategori: "SOP Instalasi", deskripsi: "", file: null, youtube_url: "" });
      setOpenUpload(false);
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengupload materi");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Hapus materi "${item.judul}"?`)) return;
    try {
      await api.delete(`/lms/materials/${item.id}`);
      toast.success("Materi berhasil dihapus");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus materi");
    }
  };

  const download = (item) => {
    window.open(`/api/lms/materials/${item.id}/download`, "_blank", "noopener,noreferrer");
  };

  const view = (item) => {
    if (item.youtube_id) {
      setPreview({ ...item, kind: "youtube", url: `https://www.youtube.com/embed/${item.youtube_id}?autoplay=1` });
      return;
    }
    const mime = String(item.mime_type || "").toLowerCase();
    const ext = String(item.filename || "").split(".").pop()?.toLowerCase();
    const supported =
      mime === "application/pdf" ||
      mime.startsWith("image/") ||
      mime.startsWith("video/") ||
      mime.startsWith("audio/");

    if (!supported) {
      toast.info("Format file ini belum bisa ditampilkan langsung. Silakan gunakan Download.");
      return;
    }

    setPreview({
      ...item,
      url: `/api/lms/materials/${item.id}/view`,
      kind: mime === "application/pdf" || ext === "pdf"
        ? "pdf"
        : mime.startsWith("video/")
          ? "video"
          : mime.startsWith("audio/")
            ? "audio"
            : "image",
    });
  };

  return (
    <div data-testid="page-lms">
      <SectionTitle
        title="LMS"
        subtitle="Pusat SOP, panduan, video tutorial, dan materi pendukung instalasi."
        right={
          isAdmin && (
            <button
              onClick={() => setOpenUpload(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
            >
              <UploadCloud className="h-4 w-4" />
              Upload Materi
            </button>
          )
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <button
          onClick={() => setCategory("ALL")}
          className={`rounded-xl border p-3 text-left transition ${category === "ALL" ? "border-blue-500 bg-blue-500/10" : "border-slate-700 bg-slate-900/40 hover:border-slate-600"}`}
        >
          <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10 text-blue-300">
            <FileText className="h-5 w-5" />
          </div>
          <div className="text-sm font-semibold text-white">Semua Materi</div>
          <div className="mt-1 text-xs text-slate-400">{counts.ALL || 0} materi</div>
        </button>

        {categories.map((name) => {
          const meta = CATEGORY_META[name] || CATEGORY_META.Lainnya;
          const Icon = meta.icon;
          return (
            <button
              key={name}
              onClick={() => setCategory(name)}
              className={`rounded-xl border p-3 text-left transition ${category === name ? "border-blue-500 bg-blue-500/10" : "border-slate-700 bg-slate-900/40 hover:border-slate-600"}`}
            >
              <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-lg border ${toneClasses[meta.tone]}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="truncate text-sm font-semibold text-white">{name}</div>
              <div className="mt-1 text-xs text-slate-400">{counts[name] || 0} materi</div>
            </button>
          );
        })}
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-slate-800 p-4 lg:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setFilter("search")(e.target.value)}
              placeholder="Cari materi, judul, deskripsi, atau kategori..."
              className="w-full rounded-lg border border-slate-700 bg-slate-950 py-2.5 pl-10 pr-3 text-sm text-white outline-none focus:border-blue-500"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setFilter("category")(e.target.value)}
            className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none"
          >
            <option value="ALL">Semua Kategori</option>
            {categories.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select
            value={sort}
            onChange={(e) => setFilter("sort")(e.target.value)}
            className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none"
          >
            <option value="NEWEST">Urutkan: Terbaru</option>
            <option value="OLDEST">Urutkan: Terlama</option>
          </select>
          <SearchButton onClick={applyFilters} testId="lms-search-button" />
        </div>

        <div className="max-h-[calc(100vh-430px)] min-h-[260px] overflow-auto">
          <div className="min-w-[920px]">
          <table className="w-full min-w-[920px] text-sm">
            <thead className="sticky top-0 z-10 bg-slate-900 text-left text-[11px] uppercase tracking-wide text-slate-400 shadow-sm">
              <tr>
                <th className="px-4 py-3">No</th>
                <th className="px-4 py-3">Judul Materi</th>
                <th className="px-4 py-3">Kategori</th>
                <th className="px-4 py-3">Deskripsi</th>
                <th className="px-4 py-3">Tanggal Upload</th>
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filtered.map((item, index) => {
                const meta = CATEGORY_META[item.kategori] || CATEGORY_META.Lainnya;
                const Icon = meta.icon;
                const kind = fileKind(item.mime_type, item.filename);
                return (
                  <tr key={item.id} className="border-t border-slate-800/80 hover:bg-slate-900/50">
                    <td className="px-4 py-4 font-mono-data text-xs text-slate-500">{index + 1}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${toneClasses[meta.tone]}`}>
                          {kind === "video" ? <Video className="h-4 w-4" /> : kind === "image" ? <ImageIcon className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                        </div>
                        <div>
                          <div className="font-semibold text-white">{item.judul}</div>
                          <div className="mt-1 text-xs text-slate-500">{item.filename}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-medium ${toneClasses[meta.tone]}`}>
                        {item.kategori}
                      </span>
                    </td>
                    <td className="max-w-[300px] px-4 py-4 text-xs leading-5 text-slate-400">{item.deskripsi || "-"}</td>
                    <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{formatDate(item.created_at)}</span>
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-400">{item.youtube_id ? "YOUTUBE" : (item.mime_type?.split("/").pop()?.toUpperCase() || "FILE")} · {item.youtube_id ? "Streaming" : formatSize(item.size)}</td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => view(item)}
                          className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/40 px-3 py-2 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/10"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </button>
                        {!item.youtube_id && (
                          <button
                            onClick={() => download(item)}
                            className="inline-flex items-center gap-2 rounded-lg border border-blue-500/50 px-3 py-2 text-xs font-semibold text-blue-300 hover:bg-blue-500/10"
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => remove(item)}
                            title="Hapus materi"
                            className="rounded-lg border border-red-500/30 p-2 text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-sm text-slate-500">
                    Belum ada materi yang sesuai.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-sm text-slate-500">Memuat materi LMS...</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </Card>

      {preview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
          <div className="flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl">
            <div className="flex shrink-0 items-center justify-between border-b border-slate-800 px-5 py-3">
              <div className="min-w-0">
                <div className="truncate font-display text-base font-semibold text-white">{preview.judul}</div>
                <div className="mt-0.5 text-xs text-slate-500">{preview.filename}</div>
              </div>
              <div className="ml-4 flex shrink-0 items-center gap-2">
                {!preview.youtube_id && (
                  <button
                    onClick={() => download(preview)}
                    className="inline-flex items-center gap-2 rounded-lg border border-blue-500/40 px-3 py-2 text-xs font-semibold text-blue-300 hover:bg-blue-500/10"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </button>
                )}
                <button
                  onClick={() => setPreview(null)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
                  title="Tutup"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 bg-slate-900">
              {preview.kind === "youtube" && (
                <div className="flex h-full w-full items-center justify-center bg-black p-4">
                  <div className="aspect-video w-full max-w-5xl overflow-hidden rounded-lg">
                    <iframe
                      title={preview.judul}
                      src={preview.url}
                      className="h-full w-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}
              {preview.kind === "pdf" && (
                <iframe
                  title={preview.judul}
                  src={preview.url}
                  className="h-full w-full border-0"
                />
              )}
              {preview.kind === "image" && (
                <div className="flex h-full w-full items-center justify-center overflow-auto p-6">
                  <img src={preview.url} alt={preview.judul} className="max-h-full max-w-full object-contain" />
                </div>
              )}
              {preview.kind === "video" && (
                <div className="flex h-full w-full items-center justify-center p-6">
                  <video src={preview.url} controls className="max-h-full max-w-full" />
                </div>
              )}
              {preview.kind === "audio" && (
                <div className="flex h-full items-center justify-center">
                  <audio src={preview.url} controls className="w-full max-w-xl" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {openUpload && isAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
              <div>
                <h2 className="font-display text-lg font-semibold text-white">Upload Materi LMS</h2>
                <p className="mt-1 text-xs text-slate-500">Hanya Super Admin yang dapat menambahkan materi.</p>
              </div>
              <button onClick={() => setOpenUpload(false)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={upload} className="space-y-4 p-5">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-300">Judul Materi</label>
                <input
                  value={form.judul}
                  onChange={(e) => setForm({ ...form, judul: e.target.value })}
                  placeholder="Contoh: SOP Instalasi Smartboard"
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-300">Kategori</label>
                <select
                  value={form.kategori}
                  onChange={(e) => setForm({ ...form, kategori: e.target.value })}
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                >
                  {categories.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-300">Deskripsi</label>
                <textarea
                  rows={3}
                  value={form.deskripsi}
                  onChange={(e) => setForm({ ...form, deskripsi: e.target.value })}
                  placeholder="Penjelasan singkat mengenai materi..."
                  className="w-full resize-none rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                />
              </div>
              <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
                <div className="mb-2 text-xs font-semibold text-blue-300">Video Tutorial via YouTube</div>
                <input
                  value={form.youtube_url}
                  onChange={(e) => setForm({ ...form, youtube_url: e.target.value, file: null })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                />
                <div className="mt-1.5 text-[11px] text-slate-500">Gunakan untuk video agar KORDA dapat streaming tanpa menyimpan file video di server.</div>
              </div>
              <div className="text-center text-[11px] text-slate-600">— atau upload file materi maksimal 100 MB —</div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-300">File Materi</label>
                <input
                  ref={inputRef}
                  type="file"
                  onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null, youtube_url: "" })}
                  className="block w-full rounded-lg border border-dashed border-slate-700 bg-slate-900 p-3 text-xs text-slate-400 file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpenUpload(false)} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800">
                  Batal
                </button>
                <button disabled={uploading} type="submit" className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
                  <UploadCloud className="h-4 w-4" />
                  {uploading ? "Mengupload..." : "Upload Materi"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
